import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useOutletContext } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PhoneInput from '@/components/PhoneInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SectionHelp from '@/components/help/SectionHelp';
import EmptyStateGuide from '@/components/help/EmptyStateGuide';
import SensitiveActionHelp from '@/components/help/SensitiveActionHelp';
import sensitiveActions from '@/help/sensitiveActions';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Plus, Archive, ChevronRight, CalendarDays, UserCheck, ArrowRightLeft, Download, ListChecks, Layers, MapPin, AlertTriangle, User, LayoutGrid, MessageSquare, PhoneCall, Sparkles, Clock, Check, FileText, Phone, Mail, Package, Trash2, Star } from 'lucide-react';
import LeadTasksModal from '@/components/crm/LeadTasksModal';

const CHANNEL_LABELS = { whatsapp: 'WhatsApp', voice: 'Llamadas', email: 'Correo', sms: 'SMS', web_chat: 'Chat web', telegram: 'Telegram' };
const CHANNEL_ICONS = { whatsapp: MessageSquare, voice: Phone, email: Mail };

const ACTIVITY_ICONS = { ia: Sparkles, asignacion: UserCheck, oportunidad: LayoutGrid, lead: FileText, llamada: PhoneCall, otro: Clock };

export default function CrmLeads() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = user?.tenant_role === 'tenant_owner';
  const [searchParams, setSearchParams] = useSearchParams();
  const { filterQuery } = useOutletContext() || {};
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('list');
  const [form, setForm] = useState({ display_name: '', email: '', phone: '', interest_summary: '', branch_id: '', catalog_item_id: '', initial_deal_value: '', stage_key: '', birthday: '' });
  // Cada lead manual debe quedar registrado en una etapa real del Pipeline
  // desde que se crea — no como paso opcional posterior. Por defecto se
  // preselecciona la primera etapa; el usuario puede cambiarla pero no
  // puede dejarla vacía.
  const [pipelineStages, setPipelineStages] = useState([]);
  const [creatingLead, setCreatingLead] = useState(false);
  const [detail, setDetail] = useState(null);
  const [agents, setAgents] = useState([]);
  const [pools, setPools] = useState([]);
  const [branches, setBranches] = useState([]);
  const [verticalLabels, setVerticalLabels] = useState({});
  const [filter, setFilter] = useState('all');
  const [poolFilter, setPoolFilter] = useState('all');
  const [reassignUserId, setReassignUserId] = useState('');
  const [poolAssignId, setPoolAssignId] = useState('');
  const [relatedLeads, setRelatedLeads] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeChannel, setActiveChannel] = useState('');
  const [summary, setSummary] = useState('');
  const [summarizing, setSummarizing] = useState(false);
  const [calling, setCalling] = useState(false);
  // Ficha 360 del Lead (cross-vertical) — feed único de actividad (humana +
  // IA + llamadas, ya no repartido en varias tarjetas) y la traza de por
  // qué se asignó a quien se asignó.
  const [activity, setActivity] = useState([]);
  const [assignmentTrace, setAssignmentTrace] = useState(null);
  const [paymentPlans, setPaymentPlans] = useState([]);
  const [planStatement, setPlanStatement] = useState(null);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [planForm, setPlanForm] = useState({ total_amount: '', down_payment_amount: '', installment_count: 12, first_due_date: '' });
  const [savingPlan, setSavingPlan] = useState(false);
  const [reconcilingId, setReconcilingId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [showLeadTasksModal, setShowLeadTasksModal] = useState(false);
  const [showDealForm, setShowDealForm] = useState(false);
  const [editingDealId, setEditingDealId] = useState(null);
  const EMPTY_VEHICLE = { vehicle_license_plate: '', vehicle_brand: '', vehicle_model: '', vehicle_year: '', vehicle_color: '', vehicle_mileage_km: '', vehicle_vin: '' };
  const [dealForm, setDealForm] = useState({ title: '', catalog_item_id: '', value_estimate: '', currency: 'MXN', ...EMPTY_VEHICLE });
  const [savingDeal, setSavingDeal] = useState(false);
  const [catalogItems, setCatalogItems] = useState([]);
  const [showLeadEditForm, setShowLeadEditForm] = useState(false);
  const [leadEditForm, setLeadEditForm] = useState({ display_name: '', email: '', phone: '', interest_summary: '', branch_id: '' });
  const [savingLead, setSavingLead] = useState(false);
  const [tenantVertical, setTenantVertical] = useState('generic');
  const isWorkshop = tenantVertical === 'workshop';
  // Marca de cliente VIP (recibe primero los avisos de lanzamiento/preventa)
  // y descarte del aviso de posible duplicado — ambos se operan desde la ficha.
  const [savingVip, setSavingVip] = useState(false);
  const [dismissingDuplicate, setDismissingDuplicate] = useState(false);
  // Fusión de duplicados (ronda 12-09)
  const [showMerge, setShowMerge] = useState(false);
  const [mergeCandidates, setMergeCandidates] = useState([]);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergeConfirmId, setMergeConfirmId] = useState(null);
  // Reparto en bloque: selección múltiple en la lista + resultado por lead
  // (el reparto puede tener éxitos y fallos en la misma operación).
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkUserId, setBulkUserId] = useState('');
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const BULK_MAX = 25;

  const load = async () => {
    try {
      const leadsUrl = `/tenant/crm/leads${filterQuery ? `?${filterQuery}` : ''}`;
      const [lRes, sRes, pRes, bRes, cRes, dRes, pipeRes] = await Promise.all([
        api.get(leadsUrl),
        api.get('/tenant/assignment/status').catch(() => ({ data: { agents_load: [], vertical_labels: {} } })),
        api.get('/tenant/assignment/pools').catch(() => ({ data: { items: [] } })),
        api.get('/tenant/locations').catch(() => ({ data: [] })),
        api.get('/tenant/catalog/items').catch(() => ({ data: [] })),
        api.get('/tenant/business/dictionary').catch(() => ({ data: { vertical: 'generic' } })),
        api.get('/tenant/crm/pipelines/default').catch(() => ({ data: { stages: [] } })),
      ]);
      setLeads(lRes.data?.items || []);
      setAgents(sRes.data?.agents_load || []);
      setVerticalLabels(sRes.data?.vertical_labels || {});
      setPools(pRes.data?.items || []);
      setBranches(Array.isArray(bRes.data) ? bRes.data : bRes.data?.items || []);
      setCatalogItems(Array.isArray(cRes.data) ? cRes.data : cRes.data?.items || []);
      setTenantVertical(dRes.data?.vertical || 'generic');
      setPipelineStages(pipeRes.data?.stages || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, [filterQuery]);

  // El encabezado del Pipeline llega hasta aqui por URL: «Nuevo lead» abre el
  // formulario y «sin responsable» deja el filtro puesto. Los parámetros se
  // borran después de aplicarlos para que recargar la página no vuelva a
  // abrir un formulario que la persona ya cerró.
  useEffect(() => {
    const abrirFormulario = searchParams.get('nuevo') === '1';
    const estado = searchParams.get('estado');
    if (!abrirFormulario && !estado) return;
    if (estado === 'unassigned' || estado === 'assigned') setFilter(estado);
    if (abrirFormulario) {
      setForm(f => ({ ...f, stage_key: f.stage_key || pipelineStages[0]?.key || '' }));
      setTab('form');
    }
    const limpio = new URLSearchParams(searchParams);
    limpio.delete('nuevo'); limpio.delete('estado');
    setSearchParams(limpio, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, pipelineStages]);

  // Si cambia el filtro, la selección anterior deja de estar a la vista:
  // se limpia para que nadie asigne leads que ya no está viendo.
  useEffect(() => {
    setSelectedIds([]); setBulkResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, poolFilter, filterQuery]);

  const handleCreate = async (runFlows = false) => {
    if (!form.display_name.trim()) { toast.error('Nombre requerido'); return; }
    setCreatingLead(true);
    try {
      const res = await api.post('/tenant/crm/leads', {
        display_name: form.display_name, email: form.email, phone: form.phone, birthday: form.birthday || undefined,
        interest_summary: form.interest_summary, branch_id: form.branch_id || null,
        run_flows: runFlows,
      });
      // Todo lead manual queda registrado en una etapa real del Pipeline
      // desde que se crea (no como paso opcional posterior) — antes solo
      // pasaba esto si además se elegía un producto/servicio de catálogo.
      const item = form.catalog_item_id ? catalogItems.find(i => i.id === form.catalog_item_id) : null;
      try {
        await api.post('/tenant/crm/deals', {
          crm_lead_id: res.data.id,
          title: `Oportunidad: ${form.display_name}`,
          catalog_item_id: form.catalog_item_id || null,
          value_estimate: form.initial_deal_value ? parseFloat(form.initial_deal_value) : (item?.price_min ?? null),
          stage_key: form.stage_key || pipelineStages[0]?.key || null,
        });
      } catch { /* el lead ya se creó; la oportunidad se puede agregar después desde su ficha */ }
      toast.success('Lead creado — se disparó el flujo de captación manual (si hay uno activo)');
      setForm({ display_name: '', email: '', phone: '', interest_summary: '', branch_id: '', catalog_item_id: '', initial_deal_value: '', stage_key: '', birthday: '' });
      setTab('list'); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error'); }
    setCreatingLead(false);
  };

  const handleArchive = async (id) => {
    try { await api.patch(`/tenant/crm/leads/${id}/archive`); toast.success('Lead archivado'); load(); setTab('list'); } catch { toast.error('Error'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/tenant/crm/leads/${id}`);
      toast.success('Lead eliminado permanentemente');
      setDetail(null); load(); setTab('list');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error al eliminar'); }
  };

  const handleReassign = async (leadId, userId) => {
    try {
      await api.patch(`/tenant/assignment/leads/${leadId}/reassign`, { user_id: userId });
      toast.success('Lead reasignado'); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error al reasignar'); }
  };

  const handlePoolAssign = async (leadId, poolId) => {
    try {
      await api.patch(`/tenant/assignment/leads/${leadId}/pool`, { pool_id: poolId || null });
      toast.success(poolId ? 'Equipo asignado' : 'Equipo removido'); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error'); }
  };

  // Reparto en bloque — el backend responde con un resultado por lead
  // (puede asignar unos y rechazar otros), así que nunca se muestra un solo
  // mensaje de éxito genérico: los que no se pudieron asignar se listan con
  // su motivo y quedan seleccionados para reintentar.
  const toggleLeadSelected = (leadId) => {
    setBulkResult(null);
    setSelectedIds(ids => (ids.includes(leadId) ? ids.filter(i => i !== leadId) : [...ids, leadId]));
  };

  const handleBulkAssign = async () => {
    if (!bulkUserId || selectedIds.length === 0 || bulkAssigning) return;
    if (selectedIds.length > BULK_MAX) { toast.error(`Selecciona máximo ${BULK_MAX} leads a la vez`); return; }
    setBulkAssigning(true); setBulkResult(null);
    try {
      const res = await api.post('/tenant/assignment/leads/bulk-assign', { lead_ids: selectedIds, user_id: bulkUserId });
      const results = res.data?.results || [];
      const assigned = res.data?.assigned ?? results.filter(r => r.ok).length;
      const failed = results.filter(r => !r.ok);
      const nameById = {};
      leads.forEach(l => { nameById[l.id] = l.display_name; });
      setBulkResult({
        assigned,
        failed: failed.map(r => ({ id: r.lead_id, name: nameById[r.lead_id] || 'Lead', error: r.error || 'No se pudo asignar' })),
      });
      if (failed.length === 0) toast.success(`${assigned} lead(s) asignados`);
      else toast.error(`${assigned} asignados y ${failed.length} sin asignar — abajo se explica por qué`);
      setSelectedIds(failed.map(r => r.lead_id));
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudieron asignar los leads'); }
    setBulkAssigning(false);
  };

  // Marca de cliente VIP — genérica, pero hoy la usan los avisos de
  // lanzamiento y preventa de Desarrollos para saber a quién avisar primero.
  const handleToggleVip = async () => {
    if (!detail?.id || savingVip) return;
    setSavingVip(true);
    try {
      const res = await api.patch(`/tenant/crm/leads/${detail.id}/vip`, { is_vip: !detail.is_vip });
      const isVip = !!res.data?.is_vip;
      setDetail(d => (d ? { ...d, is_vip: isVip } : d));
      setLeads(ls => ls.map(l => (l.id === detail.id ? { ...l, is_vip: isVip } : l)));
      toast.success(isVip ? 'Marcado como cliente VIP' : 'Ya no está marcado como VIP');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo cambiar la marca de VIP'); }
    setSavingVip(false);
  };

  const handleDismissDuplicate = async () => {
    if (!detail?.duplicate_id || dismissingDuplicate) return;
    setDismissingDuplicate(true);
    try {
      await api.patch(`/tenant/crm/duplicates/${detail.duplicate_id}/dismiss`);
      toast.success('Aviso de duplicado descartado');
      setDetail(d => (d ? { ...d, duplicate_warning: false, duplicate_warning_count: 0, duplicate_id: null } : d));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo descartar el aviso'); }
    setDismissingDuplicate(false);
  };

  const openMerge = async () => {
    if (!detail?.id) return;
    setShowMerge(true); setMergeLoading(true); setMergeCandidates([]); setMergeConfirmId(null);
    try {
      const { data } = await api.get(`/tenant/crm/leads/${detail.id}/duplicate-candidates`);
      setMergeCandidates(data?.candidates || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudieron cargar los duplicados'); }
    setMergeLoading(false);
  };

  // Fusiona el candidato (duplicado) EN el lead que estás viendo (sobreviviente).
  const doMerge = async (duplicateId) => {
    if (!detail?.id || merging) return;
    setMerging(true);
    try {
      await api.post(`/tenant/crm/leads/${detail.id}/merge`, { duplicate_id: duplicateId });
      toast.success('Leads fusionados: todo quedó en este lead');
      setShowMerge(false); setMergeConfirmId(null);
      await load();
      try {
        const { data } = await api.get(`/tenant/crm/leads/${detail.id}`);
        setDetail(data);
      } catch { /* la lista ya se recargó */ }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo fusionar'); }
    setMerging(false);
  };

  const handleExport = async () => {
    try {
      const res = await api.get('/tenant/crm/leads/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', `crm_leads_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success('CSV exportado');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error al exportar'); }
  };

  const loadRelated = async (leadId) => {
    try { const res = await api.get(`/tenant/crm/leads/${leadId}/related`); setRelatedLeads(res.data?.items || []); } catch { setRelatedLeads([]); }
  };

  const loadConversations = async (leadId) => {
    try {
      const res = await api.get(`/tenant/crm/leads/${leadId}/conversations`);
      const items = res.data?.items || [];
      setConversations(items);
      setActiveChannel(items[0]?.channel || '');
    } catch { setConversations([]); setActiveChannel(''); }
  };

  const loadTasks = async (leadId) => {
    try {
      const res = await api.get(`/tenant/crm/tasks?lead_id=${leadId}&status=all`);
      setTasks(res.data?.items || []);
    } catch { setTasks([]); }
  };

  const loadAppointments = async (leadId) => {
    try {
      const res = await api.get(`/tenant/agenda/appointments?lead_id=${leadId}`);
      setAppointments(res.data?.items || []);
    } catch { setAppointments([]); }
  };

  const loadActivity = async (leadId) => {
    try {
      const res = await api.get(`/tenant/crm/leads/${leadId}/activity`);
      setActivity(res.data?.items || []);
    } catch { setActivity([]); }
  };

  const loadAssignmentTrace = async (leadId) => {
    try {
      const res = await api.get(`/tenant/crm/leads/${leadId}/assignment-trace`);
      setAssignmentTrace(res.data?.items?.[0] || null);
    } catch { setAssignmentTrace(null); }
  };

  // Sprint 75 (3/N-4/N) ERP de Desarrollos Inmobiliarios — el backend
  // (payment_plans.py, real_estate_erp.py) ya existía sin ninguna pantalla
  // que lo usara; esta es la primera. Solo aplica al vertical real_estate.
  const loadPaymentPlans = async (leadId) => {
    try {
      const res = await api.get(`/tenant/erp/payment-plans?crm_lead_id=${leadId}`);
      const plans = res.data || [];
      setPaymentPlans(plans);
      if (plans[0]) {
        const stmt = await api.get(`/tenant/erp/payment-plans/${plans[0].id}`);
        setPlanStatement(stmt.data);
      } else { setPlanStatement(null); }
    } catch { setPaymentPlans([]); setPlanStatement(null); }
  };

  const openDetail = (l) => {
    if (!l?.id) return;
    // Aquí se limpiaban además setShowTaskForm, setNewTaskTitle,
    // setNewTaskDueDate, setNotifyOnTask y setNotifyTemplateId. Esos estados
    // dejaron de existir cuando el formulario de tareas se mudó a su propia
    // ventana (LeadTasksModal), pero las llamadas se quedaron: cinco funciones
    // que no existen. La primera reventaba y la ficha NO ABRÍA — para todos los
    // prospectos, en todas las verticales. El síntoma era «Algo salió mal»,
    // porque el error de pantalla caía en el mismo catch que la llamada al
    // servidor. La ventana de tareas limpia lo suyo al abrirse; aquí no hay
    // nada que reiniciar.
    setDetail(l); setTab('detail'); setReassignUserId(''); setPoolAssignId('');
    setSummary(''); setShowDealForm(false); setEditingDealId(null);
    setShowLeadEditForm(false);
    // cargas en paralelo; cada una con catch interno — no bloquean la ficha
    Promise.allSettled([
      loadRelated(l.id),
      loadConversations(l.id),
      loadTasks(l.id),
      loadAppointments(l.id),
      loadActivity(l.id),
      loadAssignmentTrace(l.id),
      tenantVertical === 'real_estate' ? loadPaymentPlans(l.id) : Promise.resolve(),
    ]);
    setShowPlanForm(false); setPlanForm({ total_amount: '', down_payment_amount: '', installment_count: 12, first_due_date: '' });
    if (tenantVertical !== 'real_estate') { setPaymentPlans([]); setPlanStatement(null); }
  };

  const closeDetail = () => {
    setTab('list'); setReassignUserId('');
    if (searchParams.get('lead_id')) { searchParams.delete('lead_id'); setSearchParams(searchParams, { replace: true }); }
  };

  // Deep-link (?lead_id=) — S150: abre YA sin esperar el listado ni el loading.
  // No re-abre el mismo id (evita bucle). Si falla el GET, toast + limpia URL.
  const openedLeadRef = useRef(null);
  useEffect(() => {
    const leadId = searchParams.get('lead_id');
    if (!leadId) {
      openedLeadRef.current = null;
      return;
    }
    if (openedLeadRef.current === leadId) return;
    openedLeadRef.current = leadId;

    const inList = leads.find(l => l.id === leadId);
    if (inList) {
      openDetail(inList);
      return;
    }
    // Abrir shell mínimo mientras llega el GET (evita modal blanco vacío)
    setDetail({ id: leadId, display_name: 'Cargando…' });
    setTab('detail');
    // Traer el dato y pintarlo son dos cosas distintas, y hasta hoy compartían
    // el mismo `catch`. Consecuencia medida el 17-08-2026: el servidor devolvía
    // la ficha con un 200 —comprobado en el registro de nginx— y el usuario veía
    // «Algo salió mal. Intenta de nuevo.» sin que la ventana abriera. El error
    // real era de pantalla, pero como caía en el mismo `catch` que la llamada,
    // se leía `e.response.data.detail` de un error que no venía del servidor,
    // salía vacío, y el texto acababa siendo el genérico. Nadie podía saber qué
    // pasó: ni el dueño en pantalla ni nosotros en los registros.
    api.get(`/tenant/crm/leads/${leadId}`)
      .catch((e) => {
        // Esto SÍ es del servidor.
        toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo abrir el prospecto');
        setTab('list');
        setDetail(null);
        openedLeadRef.current = null;
        return null;
      })
      .then(res => {
        if (!res) return;
        if (!res.data) {
          toast.error('Ese prospecto ya no existe');
          setTab('list');
          setDetail(null);
          openedLeadRef.current = null;
          return;
        }
        try {
          openDetail(res.data);
        } catch (err) {
          // El dato llegó bien; lo que falló es pintarlo. Se dice distinto —y se
          // deja el error de verdad en la consola— para que la próxima vez se
          // pueda arreglar en vez de adivinar.
          console.error('No se pudo pintar la ficha del prospecto', err);
          toast.error('El prospecto se cargó pero la ficha no se pudo mostrar. '
                      + 'Vuelve a intentar; si sigue, avísanos.');
          setTab('list');
          setDetail(null);
          openedLeadRef.current = null;
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSummarize = async (conversationId) => {
    if (!conversationId || summarizing) return;
    setSummarizing(true);
    try {
      const res = await api.post(`/tenant/inbox/conversations/${conversationId}/summarize`);
      const text = res.data?.summary_text || 'Sin resumen disponible.';
      setSummary(text);
      // El backend ya espeja este mismo resumen a crm_leads.ai_last_summary
      // (ver inbox_reply.py::generate_conversation_summary) — se refleja
      // aquí de inmediato sin esperar a reabrir la ficha.
      if (res.data?.summary_text) {
        setDetail(d => (d ? { ...d, ai_last_summary: text, ai_summary_updated_at: new Date().toISOString() } : d));
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo generar el resumen'); }
    setSummarizing(false);
  };

  const handleCallWithAI = async () => {
    if (!detail?.id || calling) return;
    setCalling(true);
    try {
      await api.post('/tenant/voice/calls/outbound', { lead_id: detail.id });
      toast.success('Llamando... tu asistente de IA está marcando');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo iniciar la llamada'); }
    setCalling(false);
  };

  const handleCreatePlan = async () => {
    if (!detail?.id || savingPlan) return;
    if (!planForm.total_amount || !planForm.installment_count || !planForm.first_due_date) {
      toast.error('Completa monto total, número de mensualidades y fecha de la primera cuota'); return;
    }
    setSavingPlan(true);
    try {
      await api.post('/tenant/erp/payment-plans', {
        crm_lead_id: detail.id,
        total_amount: parseFloat(planForm.total_amount),
        down_payment_amount: planForm.down_payment_amount ? parseFloat(planForm.down_payment_amount) : null,
        installment_count: parseInt(planForm.installment_count),
        first_due_date: planForm.first_due_date,
      });
      toast.success('Plan de pagos creado');
      setShowPlanForm(false);
      loadPaymentPlans(detail.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo crear el plan'); }
    setSavingPlan(false);
  };

  const handleAttachReceipt = async (installmentId) => {
    const url = window.prompt('Pega el link del comprobante (ej. imagen o PDF ya subido):');
    if (!url) return;
    try {
      await api.post(`/tenant/erp/installments/${installmentId}/receipt`, { receipt_url: url });
      toast.success('Comprobante recibido — queda en revisión hasta que un humano lo confirme');
      loadPaymentPlans(detail.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo adjuntar el comprobante'); }
  };

  const handleReconcile = async (installmentId) => {
    setReconcilingId(installmentId);
    try {
      await api.post(`/tenant/erp/installments/${installmentId}/reconcile`, {});
      toast.success('Cuota conciliada');
      loadPaymentPlans(detail.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo conciliar'); }
    setReconcilingId(null);
  };

  const handleCompleteTask = async (taskId) => {
    try { await api.patch(`/tenant/crm/tasks/${taskId}/complete`); loadTasks(detail.id); } catch { toast.error('Error'); }
  };

  const openDealForm = () => {
    setEditingDealId(null);
    setDealForm({ title: `Oportunidad: ${detail.display_name}`, catalog_item_id: '', value_estimate: '', currency: 'MXN', ...EMPTY_VEHICLE });
    setShowDealForm(true);
  };

  const openDealEditForm = async () => {
    if (!detail?.deal_id) return;
    try {
      const res = await api.get(`/tenant/crm/deals/${detail.deal_id}`);
      const d = res.data;
      const v = d.vehicle || {};
      setEditingDealId(d.id);
      setDealForm({
        title: d.title, catalog_item_id: d.catalog_item_id || '',
        value_estimate: d.value_estimate != null ? String(d.value_estimate) : '',
        currency: d.currency || 'MXN',
        vehicle_license_plate: v.license_plate || '', vehicle_brand: v.brand || '', vehicle_model: v.model || '',
        vehicle_year: v.year != null ? String(v.year) : '', vehicle_color: v.color || '',
        vehicle_mileage_km: v.mileage_km != null ? String(v.mileage_km) : '', vehicle_vin: v.vin || '',
      });
      setShowDealForm(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo cargar la oportunidad'); }
  };

  const handlePickCatalogItem = (itemId) => {
    if (itemId === 'none') { setDealForm(f => ({ ...f, catalog_item_id: '' })); return; }
    const item = catalogItems.find(i => i.id === itemId);
    setDealForm(f => ({
      ...f, catalog_item_id: itemId,
      // 2026-07-08 — price_min is a SUGGESTION only (platform owner's explicit
      // choice), so it only pre-fills an EMPTY value field — it never
      // overwrites a value the user already typed.
      value_estimate: f.value_estimate ? f.value_estimate : (item?.price_min != null ? String(item.price_min) : f.value_estimate),
      currency: item?.currency || f.currency,
    }));
  };

  const handleSaveDeal = async () => {
    if (!dealForm.title.trim() || !detail?.id) { toast.error('Título requerido'); return; }
    setSavingDeal(true);
    try {
      const payload = {
        crm_lead_id: detail.id, title: dealForm.title,
        value_estimate: dealForm.value_estimate ? parseFloat(dealForm.value_estimate) : null,
        currency: dealForm.currency, catalog_item_id: dealForm.catalog_item_id || null,
        ...(isWorkshop ? {
          vehicle_license_plate: dealForm.vehicle_license_plate || null,
          vehicle_brand: dealForm.vehicle_brand || null,
          vehicle_model: dealForm.vehicle_model || null,
          vehicle_year: dealForm.vehicle_year ? parseInt(dealForm.vehicle_year, 10) : null,
          vehicle_color: dealForm.vehicle_color || null,
          vehicle_mileage_km: dealForm.vehicle_mileage_km ? parseInt(dealForm.vehicle_mileage_km, 10) : null,
          vehicle_vin: dealForm.vehicle_vin || null,
        } : {}),
      };
      const res = editingDealId
        ? await api.put(`/tenant/crm/deals/${editingDealId}`, payload)
        : await api.post('/tenant/crm/deals', payload);
      toast.success(editingDealId ? 'Oportunidad actualizada' : 'Oportunidad creada');
      setDetail(d => ({ ...d, deal_id: res.data.id, deal_name: res.data.title }));
      setShowDealForm(false); setEditingDealId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error al guardar la oportunidad'); }
    setSavingDeal(false);
  };

  const openLeadEditForm = () => {
    setLeadEditForm({
      display_name: detail.display_name || '', email: detail.email || '', phone: detail.phone || '',
      interest_summary: detail.interest_summary || '', branch_id: detail.branch_id || '',
      birthday: detail.birthday || detail.attribution?.birthday || detail.attribution?.birth_date || '',
    });
    setShowLeadEditForm(true);
  };

  const handleSaveLeadEdit = async () => {
    if (!leadEditForm.display_name.trim() || !detail?.id) { toast.error('Nombre requerido'); return; }
    setSavingLead(true);
    try {
      const res = await api.put(`/tenant/crm/leads/${detail.id}`, { ...leadEditForm, branch_id: leadEditForm.branch_id || null });
      toast.success('Lead actualizado');
      setDetail(d => ({ ...d, ...res.data }));
      setShowLeadEditForm(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error al guardar el lead'); }
    setSavingLead(false);
  };

  const filteredLeads = leads.filter(l => {
    if (filter === 'unassigned') return !l.assigned_to_user_id;
    if (filter === 'assigned') return !!l.assigned_to_user_id;
    return true;
  }).filter(l => {
    if (poolFilter === 'none') return !l.assignment_pool_id;
    if (poolFilter !== 'all') return l.assignment_pool_id === poolFilter;
    return true;
  });

  // 2026-07-08 — "próximas tareas": a lead whose next pending task is due in
  // the future moves to its own lower section instead of mixing in with
  // leads that need attention right now (has_pending_task alone isn't
  // enough — a task due today/overdue still belongs in the main list).
  const todayIso = new Date().toISOString().slice(0, 10);
  const mainLeads = filteredLeads.filter(l => !(l.next_task_due_date && l.next_task_due_date.slice(0, 10) > todayIso));
  const upcomingLeads = filteredLeads
    .filter(l => l.next_task_due_date && l.next_task_due_date.slice(0, 10) > todayIso)
    .sort((a, b) => a.next_task_due_date.localeCompare(b.next_task_due_date));

  // Sólo se puede repartir en bloque si hay a quién asignarle los leads.
  const canBulkAssign = agents.length > 0;
  const bulkSelectableIds = filteredLeads.slice(0, BULK_MAX).map(l => l.id);
  const allVisibleSelected = bulkSelectableIds.length > 0 && bulkSelectableIds.every(id => selectedIds.includes(id));
  const toggleSelectAllVisible = () => {
    setBulkResult(null);
    setSelectedIds(allVisibleSelected ? [] : bulkSelectableIds);
  };

  const renderLeadRow = (l) => {
    const needsAttention = l.has_pending_task || !l.assigned_to_user_id;
    return (
      <div key={l.id} onClick={() => openDetail(l)}
        className={`bg-white border rounded-lg px-4 py-3 flex items-center justify-between hover:bg-[#F8FAFC] cursor-pointer ${needsAttention ? 'border-l-4 border-l-amber-400' : ''}`}
        data-testid={`lead-row-${l.id}`}>
        {canBulkAssign && (
          <label className="flex items-center pr-3 flex-shrink-0 cursor-pointer" onClick={e => e.stopPropagation()}
            title="Márcalo para asignarlo junto con otros leads a la misma persona">
            <input type="checkbox" checked={selectedIds.includes(l.id)} onChange={() => toggleLeadSelected(l.id)}
              data-testid={`lead-select-${l.id}`} />
          </label>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            {needsAttention && <span className="relative flex w-2 h-2 flex-shrink-0" title={l.has_pending_task ? 'Tiene una tarea pendiente' : 'Aún sin atender'} data-testid={`lead-attention-dot-${l.id}`}>
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
            </span>}
            {l.display_code && <span className="text-[10px] font-mono text-[#94A3B8] flex-shrink-0" title="Código de referencia — úsalo para hablar de este cliente por teléfono o WhatsApp sin dictar el ID completo">{l.display_code}</span>}
            <p className="text-sm font-bold text-[#0F172A] truncate">{l.display_name}</p>
            {l.is_vip && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400 flex-shrink-0" title="Cliente VIP — recibe primero los avisos de lanzamiento y preventa" data-testid={`lead-vip-star-${l.id}`} />}
            {l.assigned_to_name && <span className="inline-flex items-center gap-0.5 text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-1.5 py-0.5" data-testid={`lead-badge-${l.id}`}><UserCheck className="w-2.5 h-2.5" /> {l.assigned_to_name}</span>}
            {!l.assigned_to_user_id && <span className="text-[10px] bg-amber-50 text-amber-600 border border-amber-200 rounded-full px-1.5 py-0.5">Sin asignar</span>}
            {l.has_pending_task && <span className="text-[10px] bg-purple-50 text-purple-600 border border-purple-200 rounded-full px-1.5 py-0.5">Tarea pendiente</span>}
            {l.next_task_due_date && <span className="text-[10px] bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-full px-1.5 py-0.5 flex items-center gap-0.5"><CalendarDays className="w-2.5 h-2.5" /> {new Date(l.next_task_due_date).toLocaleDateString('es-MX')}</span>}
            {l.branch_name && <span className="text-[10px] bg-slate-100 text-slate-600 rounded-full px-1.5 py-0.5 flex items-center gap-0.5" data-testid="crm-leads-branch-badge"><MapPin className="w-2.5 h-2.5" /> {l.branch_name}</span>}
            {l.assignment_pool_name && <span className="text-[10px] bg-blue-50 text-blue-600 border border-blue-200 rounded-full px-1.5 py-0.5"><Layers className="w-2.5 h-2.5 inline" /> {l.assignment_pool_name}</span>}
            {l.duplicate_warning && <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" title="Posible duplicado" />}
            {l.prior_leads_count > 1 && <span className="text-[10px] text-[#94A3B8]">{l.prior_leads_count} leads</span>}
          </div>
          <div className="flex gap-2 text-xs text-[#64748B] mt-0.5">
            <span>{l.email || l.phone || l.source_type}</span>
            {l.source_name && <span className="text-[10px] text-[#94A3B8]" data-testid={`lead-attribution-${l.id}`}>{l.source_name}</span>}
            {l.attribution?.medium && <span className="text-[10px] text-[#94A3B8]">{l.attribution.medium}</span>}
            {l.attribution?.campaign_name && <span className="text-[10px] text-[#94A3B8]">{l.attribution.campaign_name}</span>}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
      </div>
    );
  };

  const appointmentLabel = verticalLabels?.appointment ? `Agendar ${verticalLabels.appointment.toLowerCase()}` : 'Agendar cita';

  // S150: con lead_id en la URL no bloqueamos la ficha con pantalla completa
  // (antes se veía «en blanco» mientras cargaba el listado).
  if (loading && !searchParams.get('lead_id') && tab !== 'detail') return <PantallaCargando />;


  return (
    <div className="space-y-4" data-testid="crm-leads">
      <SectionHelp module="crm_leads" />
      <div className="flex flex-wrap gap-2 items-center">
        <Button size="sm" onClick={() => {
          if (tab === 'form') { setTab('list'); return; }
          setForm(f => ({ ...f, stage_key: f.stage_key || pipelineStages[0]?.key || '' }));
          setTab('form');
        }} data-testid="crm-leads-new"><Plus className="w-3.5 h-3.5 mr-1" /> Nuevo lead</Button>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="crm-leads-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="unassigned">Sin responsable</SelectItem>
            <SelectItem value="assigned">Con responsable</SelectItem>
          </SelectContent>
        </Select>
        {pools.length > 0 && (
          <Select value={poolFilter} onValueChange={setPoolFilter}>
            <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="crm-leads-pool-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos equipos</SelectItem>
              <SelectItem value="none">Sin equipo</SelectItem>
              {pools.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <SensitiveActionHelp action={sensitiveActions.crm_export_leads_csv} onConfirm={handleExport}>
          <Button size="sm" variant="outline" data-testid="crm-leads-export-csv"><Download className="w-3.5 h-3.5 mr-1" /> Exportar CSV</Button>
        </SensitiveActionHelp>
      </div>

      {tab === 'form' && (
        <div className="bg-white border rounded-xl p-5 space-y-3" data-testid="crm-lead-form">
          <h3 className="text-sm font-bold">Nuevo lead manual</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><label className="text-xs text-[#64748B]">Nombre</label><Input value={form.display_name} onChange={e => setForm({...form, display_name: e.target.value})} data-testid="crm-lead-name" /></div>
            <div><label className="text-xs text-[#64748B]">Email</label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="crm-lead-email" /></div>
            <div>
              <label className="text-xs text-[#64748B]">Cumpleaños</label>
              <Input value={form.birthday || ''} onChange={e => setForm({...form, birthday: e.target.value})}
                placeholder="MM-DD o AAAA-MM-DD" data-testid="crm-lead-birthday" />
              <p className="text-[9px] text-[#94A3B8] mt-0.5">Para felicitaciones y cupones automáticos</p>
            </div>
            <div>
              <label className="text-xs text-[#64748B]">Teléfono</label>
              <PhoneInput value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                defaultCountryCode={branches.find(b => b.id === form.branch_id)?.phone_code}
                data-testid="crm-lead-phone" />
            </div>
            <div><label className="text-xs text-[#64748B]">Interés</label><Input value={form.interest_summary} onChange={e => setForm({...form, interest_summary: e.target.value})} data-testid="crm-lead-interest" /></div>
            {branches.length > 0 && (
              <div>
                <label className="text-xs text-[#64748B]">Sede</label>
                <Select value={form.branch_id || 'none'} onValueChange={v => setForm({...form, branch_id: v === 'none' ? '' : v})}>
                  <SelectTrigger className="h-8 text-xs" data-testid="crm-lead-branch"><SelectValue placeholder="Sede..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin sede</SelectItem>
                    {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {catalogItems.length > 0 && (
              <div>
                <label className="text-xs text-[#64748B]">Producto/servicio de interés (opcional)</label>
                <Select value={form.catalog_item_id || 'none'} onValueChange={v => {
                  const item = catalogItems.find(i => i.id === v);
                  setForm(f => ({ ...f, catalog_item_id: v === 'none' ? '' : v,
                    initial_deal_value: v !== 'none' && item?.price_min != null ? String(item.price_min) : f.initial_deal_value }));
                }}>
                  <SelectTrigger className="h-8 text-xs" data-testid="crm-lead-catalog"><SelectValue placeholder="Sin producto/servicio" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin producto/servicio</SelectItem>
                    {catalogItems.map(i => <SelectItem key={i.id} value={i.id}>{i.name}{i.price_min ? ` — ${i.currency} ${i.price_min.toLocaleString()}` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-[#94A3B8] mt-0.5">Crea de una vez una oportunidad con este valor para el lead.</p>
              </div>
            )}
            {form.catalog_item_id && (
              <div>
                <label className="text-xs text-[#64748B]">Valor de la oportunidad</label>
                <Input type="number" value={form.initial_deal_value} onChange={e => setForm({...form, initial_deal_value: e.target.value})} data-testid="crm-lead-deal-value" />
              </div>
            )}
            {pipelineStages.length > 0 && (
              <div>
                <label className="text-xs text-[#64748B]">Etapa del Pipeline *</label>
                <Select value={form.stage_key || pipelineStages[0]?.key} onValueChange={v => setForm({...form, stage_key: v})}>
                  <SelectTrigger className="h-8 text-xs" data-testid="crm-lead-stage"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {pipelineStages.map(s => <SelectItem key={s.key} value={s.key}>{s.name || s.key}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-[#94A3B8] mt-0.5">Todo lead manual queda registrado en el Pipeline desde que se crea — por defecto en la primera etapa.</p>
              </div>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setTab('list')}>Cancelar</Button>
            <Button size="sm" variant="outline" disabled={creatingLead} onClick={() => handleCreate(false)} data-testid="crm-lead-save">Crear</Button>
            <Button size="sm" disabled={creatingLead} onClick={() => handleCreate(true)} data-testid="crm-lead-save-run-flow"
              title="Crea el lead y arranca los flujos activos configurados para lead manual">
              Crear y correr flujo
            </Button>
          </div>
        </div>
      )}

      {tab === 'list' && filteredLeads.length === 0 && leads.length === 0 && (
        <EmptyStateGuide module="crm_leads" message="No hay leads en el CRM. Convierte leads entrantes o agrega uno manualmente." ctaLabel="Nuevo lead" onCta={() => { setForm(f => ({ ...f, stage_key: f.stage_key || pipelineStages[0]?.key || '' })); setTab('form'); }} />
      )}
      {tab === 'list' && filteredLeads.length === 0 && leads.length > 0 && (
        <p className="text-sm text-[#64748B] text-center py-8">No hay leads que coincidan con el filtro.</p>
      )}

      {/* Repartir varios leads de una vez — pensado para la bandeja "Sin responsable":
          se marcan los leads y se asignan todos a la misma persona. */}
      {tab === 'list' && filteredLeads.length > 0 && canBulkAssign && (
        <div className="bg-white border rounded-lg px-3 py-2 space-y-2" data-testid="crm-leads-bulk-bar">
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs text-[#334155] cursor-pointer">
              <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAllVisible} data-testid="crm-leads-bulk-select-all" />
              Seleccionar varios
            </label>
            {selectedIds.length === 0 ? (
              <span className="text-[11px] text-[#94A3B8]">
                Marca los leads de la lista y asígnalos todos de una vez a la misma persona. Hasta {BULK_MAX} por vez.
              </span>
            ) : (
              <>
                <span className="text-xs font-bold text-[#0F172A]" data-testid="crm-leads-bulk-count">{selectedIds.length} lead(s) seleccionados</span>
                <Select value={bulkUserId} onValueChange={setBulkUserId}>
                  <SelectTrigger className="w-[200px] h-8 text-xs" data-testid="crm-leads-bulk-user"><SelectValue placeholder="Asignar a..." /></SelectTrigger>
                  <SelectContent>{agents.filter(a => a.user_id).map(a => <SelectItem key={a.user_id} value={a.user_id}>{a.name} ({a.open_leads} leads)</SelectItem>)}</SelectContent>
                </Select>
                <SensitiveActionHelp action={sensitiveActions.assignment_bulk_assign} onConfirm={handleBulkAssign}>
                  <Button size="sm" className="h-8 text-xs" disabled={!bulkUserId || bulkAssigning || selectedIds.length > BULK_MAX} data-testid="crm-leads-bulk-assign">
                    <UserCheck className="w-3.5 h-3.5 mr-1" /> {bulkAssigning ? 'Asignando...' : 'Asignar'}
                  </Button>
                </SensitiveActionHelp>
                <button className="text-xs text-blue-500 hover:underline" onClick={() => { setSelectedIds([]); setBulkResult(null); }} data-testid="crm-leads-bulk-clear">Quitar selección</button>
              </>
            )}
          </div>
          {selectedIds.length > 0 && !bulkUserId && (
            <p className="text-[10px] text-[#94A3B8]">Elige a quién se los asignas para poder continuar.</p>
          )}
          {selectedIds.length > BULK_MAX && (
            <p className="text-[10px] text-amber-600">Selecciona máximo {BULK_MAX} leads a la vez. Quita {selectedIds.length - BULK_MAX} para poder asignarlos.</p>
          )}
          {bulkResult && (
            <div className="text-[11px] space-y-0.5 border-t border-[#F1F5F9] pt-1.5" data-testid="crm-leads-bulk-result">
              <p className="text-emerald-700">{bulkResult.assigned} lead(s) asignados.</p>
              {bulkResult.failed.length > 0 && (
                <>
                  <p className="text-amber-700">{bulkResult.failed.length} no se pudieron asignar (siguen seleccionados para reintentar):</p>
                  {bulkResult.failed.map(f => (
                    <p key={f.id} className="text-[#64748B] pl-2">· {f.name}: {f.error}</p>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'list' && filteredLeads.length > 0 && (
        <div className="space-y-2" data-testid="crm-leads-list">
          {mainLeads.map(renderLeadRow)}
        </div>
      )}

      {tab === 'list' && upcomingLeads.length > 0 && (
        <div className="space-y-2 pt-2" data-testid="crm-leads-upcoming">
          <h4 className="text-xs font-bold text-[#64748B] flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> Próximas tareas ({upcomingLeads.length})</h4>
          {upcomingLeads.map(renderLeadRow)}
        </div>
      )}

      <Dialog open={tab === 'detail' && !!detail} onOpenChange={(open) => { if (!open) closeDetail(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] min-h-[240px] overflow-y-auto z-[100]" data-testid="crm-lead-detail">
        {detail && (
        <>
          <DialogHeader>
            <DialogTitle>
              {detail.display_name || 'Lead'}
              {detail.display_code && <span className="ml-2 text-xs font-mono font-normal text-[#94A3B8]" title="Código de referencia corto">{detail.display_code}</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><span className="text-[#64748B]">Email:</span> {detail.email || '—'}</div>
            <div><span className="text-[#64748B]">Teléfono:</span> {detail.phone || '—'}</div>
            <div><span className="text-[#64748B]">Fuente:</span> {detail.source_type}</div>
            <div><span className="text-[#64748B]">Origen:</span> {detail.source_name || '—'}</div>
            <div><span className="text-[#64748B]">Interés:</span> {detail.interest_summary || '—'}</div>
            {detail.branch_name && <div><span className="text-[#64748B]">Sede:</span> {detail.branch_name}</div>}
            {detail.attribution?.medium && <div><span className="text-[#64748B]">Medio:</span> {detail.attribution.medium}</div>}
            {detail.attribution?.channel && <div><span className="text-[#64748B]">Canal:</span> {detail.attribution.channel}</div>}
            {detail.attribution?.campaign_name && <div><span className="text-[#64748B]">Campaña:</span> {detail.attribution.campaign_name}</div>}
            {detail.attribution?.campaign_code && <div><span className="text-[#64748B]">Código:</span> {detail.attribution.campaign_code}</div>}
            <div data-testid="lead-birthday">
              <span className="text-[#64748B]">Cumpleaños:</span>{' '}
              {detail.birthday || detail.attribution?.birthday || detail.attribution?.birth_date || (
                <span className="text-amber-600">sin fecha — necesario para recetas de cumpleaños</span>
              )}
            </div>
            <div className="col-span-2">
              <span className="text-[#64748B]">Responsable:</span>{' '}
              {detail.assigned_to_name ? <span className="font-medium text-emerald-700">{detail.assigned_to_name}</span> : <span className="text-amber-600">Sin asignar</span>}
              {assignmentTrace && (
                <span className="text-[#94A3B8] italic ml-1" data-testid="lead-assignment-trace" title={assignmentTrace.summary || ''}>
                  · {assignmentTrace.strategy_label}{assignmentTrace.assigned_by_name ? ` por ${assignmentTrace.assigned_by_name}` : ''}
                </span>
              )}
            </div>
            {detail.assignment_pool_name && <div className="col-span-2"><span className="text-[#64748B]">Equipo:</span> <span className="font-medium text-blue-600">{detail.assignment_pool_name}</span></div>}
          </div>

          {/* Resumen de IA — espejo del resumen que Inbox genera, visible de un vistazo sin entrar a la conversación */}
          {detail.ai_last_summary && (
            <div className="p-2.5 bg-violet-50 border border-violet-200 rounded-lg flex items-start gap-2" data-testid="lead-ai-summary">
              <Sparkles className="w-3.5 h-3.5 text-violet-500 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-xs text-violet-900">{detail.ai_last_summary}</p>
                {detail.ai_summary_updated_at && (
                  <p className="text-[10px] text-violet-400 mt-0.5">Resumen de IA · {new Date(detail.ai_summary_updated_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}</p>
                )}
              </div>
            </div>
          )}

          {/* Plan de pagos — ERP de Desarrollos Inmobiliarios (Sprint 75 3/N-4/N).
              El backend ya existía sin ninguna pantalla que lo usara. */}
          {tenantVertical === 'real_estate' && (
            <div className="p-3 bg-white border border-[#E2E8F0] rounded-lg space-y-2" data-testid="lead-payment-plan-section">
              <p className="text-xs font-bold text-[#334155]">Plan de pagos</p>
              {planStatement ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[#64748B]">Total: <strong className="text-[#0F172A]">{planStatement.plan.currency} {planStatement.plan.total_amount.toLocaleString('es-MX')}</strong></span>
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${planStatement.plan.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{planStatement.plan.status}</span>
                  </div>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {planStatement.installments.map(inst => {
                      const badge = { pending: 'bg-gray-100 text-gray-600', overdue: 'bg-red-50 text-red-600', reconciling: 'bg-amber-50 text-amber-700', paid: 'bg-emerald-50 text-emerald-700' }[inst.status] || 'bg-gray-100 text-gray-600';
                      return (
                        <div key={inst.id} className="flex items-center justify-between text-[11px] py-1 border-b border-[#F1F5F9]" data-testid={`installment-${inst.installment_number}`}>
                          <span className="text-[#334155]">#{inst.installment_number} · {inst.due_date} · {planStatement.plan.currency} {inst.amount.toLocaleString('es-MX')}</span>
                          <span className="flex items-center gap-1.5">
                            <span className={`px-1.5 py-0.5 rounded-full ${badge}`}>{inst.status}</span>
                            {inst.status === 'pending' && <button className="text-blue-500 hover:underline" onClick={() => handleAttachReceipt(inst.id)} data-testid={`installment-receipt-${inst.installment_number}`}>Subir comprobante</button>}
                            {inst.status === 'reconciling' && <button className="text-emerald-600 hover:underline" disabled={reconcilingId === inst.id} onClick={() => handleReconcile(inst.id)} data-testid={`installment-reconcile-${inst.installment_number}`}>{reconcilingId === inst.id ? 'Conciliando...' : 'Confirmar pago'}</button>}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : showPlanForm ? (
                <div className="p-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="text-[10px] text-[#64748B]">Monto total</label><Input type="number" value={planForm.total_amount} onChange={e => setPlanForm({ ...planForm, total_amount: e.target.value })} className="h-7 text-xs" data-testid="plan-total-amount" /></div>
                    <div><label className="text-[10px] text-[#64748B]">Enganche (opcional)</label><Input type="number" value={planForm.down_payment_amount} onChange={e => setPlanForm({ ...planForm, down_payment_amount: e.target.value })} className="h-7 text-xs" data-testid="plan-down-payment" /></div>
                    <div><label className="text-[10px] text-[#64748B]">Mensualidades</label><Input type="number" min="1" value={planForm.installment_count} onChange={e => setPlanForm({ ...planForm, installment_count: e.target.value })} className="h-7 text-xs" data-testid="plan-installment-count" /></div>
                    <div><label className="text-[10px] text-[#64748B]">Primera cuota vence</label><Input type="date" value={planForm.first_due_date} onChange={e => setPlanForm({ ...planForm, first_due_date: e.target.value })} className="h-7 text-xs" data-testid="plan-first-due-date" /></div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowPlanForm(false)}>Cancelar</Button>
                    <Button size="sm" className="h-7 text-xs" onClick={handleCreatePlan} disabled={savingPlan} data-testid="plan-save">{savingPlan ? 'Guardando...' : 'Crear plan'}</Button>
                  </div>
                </div>
              ) : (
                <Button size="sm" variant="outline" className="text-xs" onClick={() => setShowPlanForm(true)} data-testid="crm-lead-open-plan-form">+ Crear plan de pagos</Button>
              )}
            </div>
          )}

          {/* Cliente VIP — hoy lo usan los avisos de lanzamiento y preventa que
              se envían desde Desarrollos: sin esta marca nadie los recibe. */}
          <div className={`flex items-center gap-2 p-2.5 rounded-lg border ${detail.is_vip ? 'bg-amber-50 border-amber-200' : 'bg-[#F8FAFC] border-[#E2E8F0]'}`} data-testid="crm-lead-vip-section">
            <Star className={`w-4 h-4 flex-shrink-0 ${detail.is_vip ? 'text-amber-500 fill-amber-400' : 'text-[#94A3B8]'}`} />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold text-[#334155]">{detail.is_vip ? 'Es cliente VIP' : 'Cliente VIP'}</p>
              <p className="text-[10px] text-[#94A3B8]">Los clientes VIP reciben primero los avisos de lanzamiento y preventa. Márcalo para tus clientes de mayor valor.</p>
            </div>
            <Button size="sm" variant="outline" className="h-7 text-xs flex-shrink-0" onClick={handleToggleVip} disabled={savingVip} data-testid="crm-lead-vip-toggle">
              {savingVip ? 'Guardando...' : detail.is_vip ? 'Quitar VIP' : 'Marcar como VIP'}
            </Button>
          </div>

          {!showLeadEditForm ? (
            <Button size="sm" variant="outline" className="text-xs" onClick={openLeadEditForm} data-testid="crm-lead-open-edit-form"><User className="w-3.5 h-3.5 mr-1" /> Modificar datos del lead</Button>
          ) : (
            <div className="p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg space-y-2" data-testid="crm-lead-edit-form">
              <p className="text-xs font-bold text-[#334155]">Modificar datos del lead</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div><label className="text-[10px] text-[#64748B]">Nombre</label><Input value={leadEditForm.display_name} onChange={e => setLeadEditForm(f => ({ ...f, display_name: e.target.value }))} className="h-7 text-xs" data-testid="crm-lead-edit-name" /></div>
                <div><label className="text-[10px] text-[#64748B]">Email</label><Input value={leadEditForm.email} onChange={e => setLeadEditForm(f => ({ ...f, email: e.target.value }))} className="h-7 text-xs" data-testid="crm-lead-edit-email" /></div>
                <div>
                  <label className="text-[10px] text-[#64748B]">Cumpleaños</label>
                  <Input
                    value={leadEditForm.birthday || ''}
                    onChange={e => setLeadEditForm(f => ({ ...f, birthday: e.target.value }))}
                    placeholder="MM-DD o AAAA-MM-DD"
                    className="h-7 text-xs"
                    data-testid="crm-lead-edit-birthday"
                  />
                  <p className="text-[9px] text-[#94A3B8] mt-0.5">Para felicitar o enviar cupón automático</p>
                </div>
                <div><label className="text-[10px] text-[#64748B]">Teléfono</label><PhoneInput value={leadEditForm.phone} onChange={e => setLeadEditForm(f => ({ ...f, phone: e.target.value }))}
                  defaultCountryCode={branches.find(b => b.id === leadEditForm.branch_id)?.phone_code} data-testid="crm-lead-edit-phone" /></div>
                <div><label className="text-[10px] text-[#64748B]">Interés</label><Input value={leadEditForm.interest_summary} onChange={e => setLeadEditForm(f => ({ ...f, interest_summary: e.target.value }))} className="h-7 text-xs" data-testid="crm-lead-edit-interest" /></div>
                {branches.length > 0 && (
                  <div>
                    <label className="text-[10px] text-[#64748B]">Sede</label>
                    <Select value={leadEditForm.branch_id || 'none'} onValueChange={v => setLeadEditForm(f => ({ ...f, branch_id: v === 'none' ? '' : v }))}>
                      <SelectTrigger className="h-7 text-xs" data-testid="crm-lead-edit-branch"><SelectValue placeholder="Sede..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin sede</SelectItem>
                        {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowLeadEditForm(false)}>Cancelar</Button>
                <Button size="sm" className="h-7 text-xs" onClick={handleSaveLeadEdit} disabled={savingLead} data-testid="crm-lead-edit-save">{savingLead ? 'Guardando...' : 'Guardar'}</Button>
              </div>
            </div>
          )}

          {/* C1: Deal CTA */}
          {detail.deal_id && !showDealForm && (
            <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg border border-emerald-200">
              <LayoutGrid className="w-4 h-4 text-emerald-600" />
              <div className="flex-1"><p className="text-xs font-medium text-emerald-700">{detail.deal_name}</p></div>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate('/tenant/crm/pipeline')}>Ver oportunidad</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={openDealEditForm} data-testid="crm-lead-edit-deal-btn">Modificar oportunidad</Button>
            </div>
          )}
          {!detail.deal_id && !showDealForm && (
            <Button size="sm" variant="outline" className="text-xs" onClick={openDealForm} data-testid="crm-lead-open-deal-form"><LayoutGrid className="w-3.5 h-3.5 mr-1" /> Crear oportunidad</Button>
          )}
          {showDealForm && (
            <div className="p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg space-y-2" data-testid="crm-lead-deal-form">
              <p className="text-xs font-bold text-[#334155]">{editingDealId ? 'Modificar oportunidad' : 'Nueva oportunidad'}</p>
              <div><label className="text-[10px] text-[#64748B]">Título</label><Input value={dealForm.title} onChange={e => setDealForm(f => ({ ...f, title: e.target.value }))} className="h-7 text-xs" data-testid="crm-deal-title" /></div>
              {catalogItems.length > 0 && (
                <div>
                  <label className="text-[10px] text-[#64748B] flex items-center gap-1"><Package className="w-2.5 h-2.5" /> Producto o servicio (opcional)</label>
                  <Select value={dealForm.catalog_item_id || 'none'} onValueChange={handlePickCatalogItem}>
                    <SelectTrigger className="h-7 text-xs" data-testid="crm-deal-catalog-select"><SelectValue placeholder="Ninguno..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Ninguno</SelectItem>
                      {catalogItems.map(i => <SelectItem key={i.id} value={i.id}>{i.name}{i.price_min ? ` — ${i.currency} ${i.price_min.toLocaleString()}` : ''}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="text-[10px] text-[#64748B]">Valor estimado</label>
                  <Input type="number" value={dealForm.value_estimate} onChange={e => setDealForm(f => ({ ...f, value_estimate: e.target.value }))} className="h-7 text-xs" placeholder="0.00" data-testid="crm-deal-value" />
                  {dealForm.catalog_item_id && <p className="text-[9px] text-[#94A3B8] mt-0.5">Sugerido desde el catálogo — puedes editarlo.</p>}
                </div>
                <div className="w-20">
                  <label className="text-[10px] text-[#64748B]">Moneda</label>
                  <Input value={dealForm.currency} onChange={e => setDealForm(f => ({ ...f, currency: e.target.value }))} className="h-7 text-xs" />
                </div>
              </div>
              {isWorkshop && (
                <div className="pt-1 border-t border-[#E2E8F0] space-y-2" data-testid="crm-deal-vehicle-section">
                  <p className="text-[10px] font-bold text-[#334155] pt-1">Vehículo del cliente</p>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="text-[10px] text-[#64748B]">Placa</label>
                      <Input value={dealForm.vehicle_license_plate} onChange={e => setDealForm(f => ({ ...f, vehicle_license_plate: e.target.value }))} className="h-7 text-xs" data-testid="crm-deal-vehicle-plate" />
                    </div>
                    <div className="flex-1">
                      <label className="text-[10px] text-[#64748B]">Marca</label>
                      <Input value={dealForm.vehicle_brand} onChange={e => setDealForm(f => ({ ...f, vehicle_brand: e.target.value }))} className="h-7 text-xs" />
                    </div>
                    <div className="flex-1">
                      <label className="text-[10px] text-[#64748B]">Modelo</label>
                      <Input value={dealForm.vehicle_model} onChange={e => setDealForm(f => ({ ...f, vehicle_model: e.target.value }))} className="h-7 text-xs" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="w-16">
                      <label className="text-[10px] text-[#64748B]">Año</label>
                      <Input type="number" value={dealForm.vehicle_year} onChange={e => setDealForm(f => ({ ...f, vehicle_year: e.target.value }))} className="h-7 text-xs" />
                    </div>
                    <div className="flex-1">
                      <label className="text-[10px] text-[#64748B]">Color</label>
                      <Input value={dealForm.vehicle_color} onChange={e => setDealForm(f => ({ ...f, vehicle_color: e.target.value }))} className="h-7 text-xs" />
                    </div>
                    <div className="w-24">
                      <label className="text-[10px] text-[#64748B]">Kilometraje</label>
                      <Input type="number" value={dealForm.vehicle_mileage_km} onChange={e => setDealForm(f => ({ ...f, vehicle_mileage_km: e.target.value }))} className="h-7 text-xs" />
                    </div>
                  </div>
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setShowDealForm(false); setEditingDealId(null); }}>Cancelar</Button>
                <Button size="sm" className="h-7 text-xs" onClick={handleSaveDeal} disabled={savingDeal} data-testid="crm-deal-save">{savingDeal ? 'Guardando...' : 'Guardar'}</Button>
              </div>
            </div>
          )}

          {/* Duplicate warning */}
          {detail.duplicate_warning && (
            <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg border border-amber-200" data-testid="crm-lead-duplicate-banner">
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <p className="text-xs text-amber-700 flex-1">
                Posible duplicado detectado. Revisa el historial del contacto.
                {detail.duplicate_id && <span className="block text-[10px] text-amber-600/80">Si ya lo revisaste y no es el mismo cliente, descarta el aviso para que deje de aparecer.</span>}
              </p>
              <Button size="sm" variant="outline" className="h-7 text-xs flex-shrink-0" onClick={openMerge} data-testid="crm-lead-merge-open">
                Fusionar
              </Button>
              {detail.duplicate_id && (
                <SensitiveActionHelp action={sensitiveActions.crm_duplicate_warning_dismiss} onConfirm={handleDismissDuplicate}>
                  <Button size="sm" variant="outline" className="h-7 text-xs flex-shrink-0" disabled={dismissingDuplicate} data-testid="crm-lead-duplicate-dismiss">
                    {dismissingDuplicate ? 'Descartando...' : 'Descartar aviso'}
                  </Button>
                </SensitiveActionHelp>
              )}
            </div>
          )}

          {/* Fusionar duplicados — mueve todo al lead que estás viendo */}
          <Dialog open={showMerge} onOpenChange={(v) => { setShowMerge(v); if (!v) setMergeConfirmId(null); }}>
            <DialogContent className="max-w-lg" data-testid="crm-merge-dialog">
              <DialogHeader>
                <DialogTitle>Fusionar duplicados en «{detail?.display_name}»</DialogTitle>
              </DialogHeader>
              <p className="text-xs text-[#64748B]">
                Elige el lead duplicado a unir. Sus conversaciones, oportunidades, citas,
                tareas y actividad se moverán a <b>este</b> lead, y el duplicado quedará
                archivado. <b>No se puede deshacer.</b>
              </p>
              {mergeLoading ? (
                <p className="text-sm text-[#94A3B8] py-4">Buscando duplicados…</p>
              ) : mergeCandidates.length === 0 ? (
                <p className="text-sm text-[#94A3B8] py-4">No se encontraron otros leads activos con el mismo teléfono o contacto.</p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {mergeCandidates.map((c) => (
                    <div key={c.id} className="border border-[#E2E8F0] rounded-lg p-2.5" data-testid={`crm-merge-candidate-${c.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[#0F172A] truncate">{c.display_name || 'Sin nombre'}</p>
                          <p className="text-[11px] text-[#64748B]">
                            {c.phone || 'sin teléfono'} · {c.conversations} conv · {c.open_deals} oport. abiertas · {c.appointments} citas
                            {c.has_open_deal && <span className="ml-1 text-emerald-600">• oportunidad activa</span>}
                          </p>
                        </div>
                        {mergeConfirmId === c.id ? (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button size="sm" className="h-7 text-xs bg-red-600 hover:bg-red-700" disabled={merging} onClick={() => doMerge(c.id)} data-testid={`crm-merge-confirm-${c.id}`}>
                              {merging ? 'Fusionando…' : 'Confirmar'}
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={merging} onClick={() => setMergeConfirmId(null)}>Cancelar</Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" className="h-7 text-xs flex-shrink-0" onClick={() => setMergeConfirmId(c.id)} data-testid={`crm-merge-pick-${c.id}`}>
                            Fusionar aquí
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Contact link */}
          {detail.contact_id && (
            <button onClick={() => navigate(`/tenant/crm/contacts/${detail.contact_id}`)} className="text-xs text-blue-500 hover:underline flex items-center gap-1">
              <User className="w-3 h-3" /> Ver contacto ({detail.contact_display_name || 'Timeline'})
            </button>
          )}

          {/* Conversations — one tab per channel; same channel always reuses its thread */}
          {conversations.length > 0 && (
            <div className="space-y-2" data-testid="lead-conversations-section">
              <p className="text-xs font-bold text-[#334155]">Conversaciones</p>
              <Tabs value={activeChannel} onValueChange={setActiveChannel}>
                <TabsList data-testid="lead-conversation-tabs">
                  {[...new Set(conversations.map(c => c.channel))].map(ch => {
                    const Icon = CHANNEL_ICONS[ch] || MessageSquare;
                    return (
                      <TabsTrigger key={ch} value={ch} data-testid={`lead-conversation-tab-${ch}`}>
                        <Icon className="w-3.5 h-3.5 mr-1" /> {CHANNEL_LABELS[ch] || ch}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
                {[...new Set(conversations.map(c => c.channel))].map(ch => (
                  <TabsContent key={ch} value={ch} className="space-y-2">
                    {conversations.filter(c => c.channel === ch).map(conv => (
                      <div key={conv.id} className="p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg space-y-2" data-testid="lead-conversation-card">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] bg-slate-100 text-slate-600 rounded-full px-1.5 py-0.5">{conv.status}</span>
                          <span className="text-[10px] text-[#94A3B8]">{conv.last_message_at ? new Date(conv.last_message_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : ''}</span>
                        </div>
                        {conv.last_message_preview && <p className="text-xs text-[#64748B] truncate">"{conv.last_message_preview}"</p>}
                        <div className="flex gap-2 flex-wrap">
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate(`/tenant/inbox?c=${conv.id}`)}>Retomar conversación</Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleSummarize(conv.id)} disabled={summarizing} data-testid="lead-summarize-btn">
                            <Sparkles className="w-3 h-3 mr-1" /> {summarizing ? 'Resumiendo...' : 'Resumen con IA'}
                          </Button>
                        </div>
                        {summary && <p className="text-xs text-[#334155] bg-white border border-[#E2E8F0] rounded p-2">{summary}</p>}
                      </div>
                    ))}
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          )}

          {/* Related leads (contact history) */}
          {relatedLeads.length > 0 && (
            <div className="space-y-1" data-testid="contact-lead-history">
              <p className="text-xs font-bold text-[#64748B]">Historial del contacto ({relatedLeads.length} leads previos)</p>
              {relatedLeads.slice(0, 5).map(r => (
                <div key={r.id} className="text-xs flex items-center gap-2 py-0.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${r.status === 'active' ? 'bg-emerald-400' : 'bg-gray-300'}`} />
                  <span className="text-[#0F172A]">{r.display_name}</span>
                  <span className="text-[#94A3B8]">{r.status} · {r.created_at ? new Date(r.created_at).toLocaleDateString('es-MX') : ''}</span>
                </div>
              ))}
            </div>
          )}

          {/* Tasks — se gestionan en modal sin salir del CRM */}
          <div className="space-y-1.5" data-testid="lead-tasks-section">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold text-[#64748B]">Tareas ({tasks.length})</p>
              <button
                type="button"
                className="text-xs text-blue-500 hover:underline"
                onClick={() => setShowLeadTasksModal(true)}
                data-testid="lead-task-open-modal"
              >
                Ver / crear →
              </button>
            </div>
            {tasks.slice(0, 5).map(t => (
              <div
                key={t.id}
                role="button"
                tabIndex={0}
                onClick={() => setShowLeadTasksModal(true)}
                onKeyDown={(e) => e.key === 'Enter' && setShowLeadTasksModal(true)}
                className="flex items-center gap-2 text-xs py-0.5 cursor-pointer hover:bg-[#F8FAFC] rounded px-0.5"
                data-testid={`lead-task-${t.id}`}
              >
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleCompleteTask(t.id); }}
                  disabled={t.status === 'done'}
                  className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${t.status === 'done' ? 'bg-emerald-500 border-emerald-500' : 'border-[#CBD5E1]'}`}
                >
                  {t.status === 'done' && <Check className="w-2.5 h-2.5 text-white" />}
                </button>
                <span className={t.status === 'done' ? 'line-through text-[#94A3B8]' : 'text-[#0F172A]'}>{t.title}</span>
                {(t.due_at || t.due_date) && (
                  <span className="text-[#94A3B8]">
                    · {t.due_at
                      ? new Date(t.due_at).toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                      : new Date(t.due_date).toLocaleDateString('es-MX')}
                  </span>
                )}
              </div>
            ))}
            {tasks.length === 0 && (
              <p className="text-xs text-[#94A3B8]">
                Sin tareas.{' '}
                <button type="button" className="text-blue-500 hover:underline" onClick={() => setShowLeadTasksModal(true)}>
                  Crear sin salir del CRM
                </button>
              </p>
            )}
          </div>
          <LeadTasksModal
            open={showLeadTasksModal}
            lead={detail}
            onClose={() => setShowLeadTasksModal(false)}
            onChanged={() => { if (detail?.id) { loadTasks(detail.id); load(); } }}
          />

          {/* Appointments */}
          {appointments.length > 0 && (
            <div className="space-y-1" data-testid="lead-appointments-section">
              <p className="text-xs font-bold text-[#64748B]">{appointmentLabel}s ({appointments.length})</p>
              {appointments.slice(0, 5).map(a => (
                <div key={a.id} className="flex items-center gap-2 text-xs py-0.5">
                  <CalendarDays className="w-3 h-3 text-[#94A3B8] flex-shrink-0" />
                  <span className="text-[#0F172A]">{new Date(a.starts_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}</span>
                  <span className="text-[#94A3B8]">· {a.status}</span>
                </div>
              ))}
            </div>
          )}

          {/* Actividad — un solo feed cronológico (humano + IA + llamadas), en vez
              de tarjetas separadas por tipo como en otros CRMs del mismo giro */}
          {activity.length > 0 && (
            <div className="space-y-1" data-testid="lead-activity-section">
              <p className="text-xs font-bold text-[#64748B]">Actividad</p>
              <div className="space-y-1.5 max-h-56 overflow-y-auto">
                {activity.map((item, idx) => {
                  const Icon = ACTIVITY_ICONS[item.category] || Clock;
                  return (
                    <div key={idx} className="flex items-start gap-2 text-xs py-0.5" data-testid={`lead-activity-item-${idx}`}>
                      <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${item.category === 'ia' ? 'text-violet-500' : item.category === 'llamada' ? 'text-blue-500' : 'text-[#94A3B8]'}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-[#0F172A]">
                          {item.summary}
                          {item.category === 'ia' && <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded bg-violet-100 text-violet-600 font-medium align-middle">IA</span>}
                        </p>
                        {item.kind === 'call' && item.recording_url && (
                          <a href={item.recording_url} target="_blank" rel="noreferrer" className="text-[10px] text-blue-500 hover:underline">Escuchar grabación</a>
                        )}
                      </div>
                      <span className="text-[#94A3B8] flex-shrink-0 text-[10px]">
                        {item.actor_name && <span className="mr-1">{item.actor_name} ·</span>}
                        {item.created_at ? new Date(item.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
              {detail.contact_id && (
                <button onClick={() => navigate(`/tenant/crm/contacts/${detail.contact_id}`)} className="text-xs text-blue-500 hover:underline">Ver historial completo del contacto</button>
              )}
            </div>
          )}

          {/* Reassign */}
          {agents.length > 0 && (
            <div className="flex items-center gap-2" data-testid="crm-lead-reassign-section">
              <Select value={reassignUserId} onValueChange={setReassignUserId}>
                <SelectTrigger className="w-[200px] h-8 text-xs" data-testid="crm-lead-reassign-select"><SelectValue placeholder="Reasignar a..." /></SelectTrigger>
                <SelectContent>{agents.filter(a => a.user_id).map(a => <SelectItem key={a.user_id} value={a.user_id}>{a.name} ({a.open_leads} leads)</SelectItem>)}</SelectContent>
              </Select>
              {reassignUserId && (
                <SensitiveActionHelp action={sensitiveActions.assignment_reassign} onConfirm={() => { handleReassign(detail.id, reassignUserId); setReassignUserId(''); }}>
                  <Button size="sm" variant="outline" data-testid="crm-lead-reassign-confirm"><ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Reasignar</Button>
                </SensitiveActionHelp>
              )}
            </div>
          )}

          {/* Pool assign */}
          {pools.length > 0 && (
            <div className="flex items-center gap-2">
              <Select value={poolAssignId || 'current'} onValueChange={setPoolAssignId}>
                <SelectTrigger className="w-[200px] h-8 text-xs" data-testid="crm-lead-pool-select"><SelectValue placeholder="Equipo..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="current">{detail.assignment_pool_name || 'Sin equipo'}</SelectItem>
                  <SelectItem value="none">Sin equipo</SelectItem>
                  {pools.filter(p => p.id !== detail.assignment_pool_id).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {poolAssignId && poolAssignId !== 'current' && (
                <SensitiveActionHelp action={sensitiveActions.assignment_pool_change} onConfirm={() => { handlePoolAssign(detail.id, poolAssignId === 'none' ? null : poolAssignId); setPoolAssignId(''); }}>
                  <Button size="sm" variant="outline" className="h-8 text-xs">Cambiar equipo</Button>
                </SensitiveActionHelp>
              )}
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            <Button size="sm" onClick={() => navigate(`/tenant/agenda/new?lead_id=${detail.id}`)} data-testid="crm-lead-schedule"><CalendarDays className="w-3.5 h-3.5 mr-1" /> {appointmentLabel}</Button>
            {/* Sprint 57 (2/N) — venta directa desde la ficha del lead, el
                Punto de Venta lee ?crm_lead_id= y preselecciona este cliente. */}
            <Button size="sm" variant="outline" onClick={() => navigate(`/tenant/pos?crm_lead_id=${detail.id}${detail.deal_id ? `&crm_deal_id=${detail.deal_id}` : ''}`)} data-testid="crm-lead-create-sale">
              <Package className="w-3.5 h-3.5 mr-1" /> Crear venta
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowLeadTasksModal(true)} data-testid="crm-lead-new-task"><ListChecks className="w-3.5 h-3.5 mr-1" /> Tareas</Button>
            {detail.phone && (
              <Button size="sm" variant="outline" onClick={handleCallWithAI} disabled={calling} data-testid="crm-lead-call-ai">
                <PhoneCall className="w-3.5 h-3.5 mr-1" /> {calling ? 'Llamando...' : 'Llamar con IA'}
              </Button>
            )}
            {detail.status !== 'archived' && <Button size="sm" variant="outline" onClick={() => handleArchive(detail.id)} data-testid="crm-lead-archive"><Archive className="w-3.5 h-3.5 mr-1" /> Archivar</Button>}
            {/* Sprint 53 (3/N) — borrado real, solo visible para el Owner */}
            {isOwner && (
              <SensitiveActionHelp action={sensitiveActions.lead_delete_permanent} onConfirm={() => handleDelete(detail.id)}>
                <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="crm-lead-delete">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
                </Button>
              </SensitiveActionHelp>
            )}
            <Button size="sm" variant="outline" onClick={closeDetail} data-testid="crm-lead-close">Cerrar</Button>
          </div>
          </div>
        </>
        )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
